import React, { useState } from 'react';
import { LoginProviders } from './LoginProviders/LoginProviders';
import { Register } from './Register/Register';
import { LoginEmailPassword } from './LoginEmailPassword/LoginEmailPassword';
import styles from './LoginStandAlone.module.scss';
import { ForgotPassword } from './ForgotPassword/ForgotPassword';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { LoginDisclaimer } from './LoginDisclaimer/LoginDisclaimer';
import { ForgotPasswordSuccess } from './ForgotPasswordSuccess/ForgotPasswordSuccess';
import { ELoginProvidersViewType } from '@/models';
import { useAnalytics } from '@/hooks/analytics/useAnalytics';
import { ANALYTICS } from '@/constants/analytics/analytics';
import { useUser } from '@/hooks';

enum ELoginViewType {
  login = 'login',
  register = 'register',
  forgotPassword = 'forgotPassword',
  forgotPasswordSuccess = 'forgotPasswordSuccess',
}
interface ILoginStandAloneProps {
  onSuccess: () => void;
  showTitles: boolean;
  providersViewType: ELoginProvidersViewType;
  analyticsPayload: {
    source: string;
    additionalData?: Record<string, unknown>;
  };
}
export const LoginStandAlone: React.FC<ILoginStandAloneProps> = (
  props: ILoginStandAloneProps
) => {
  const { isPremium } = useUser();
  const { fireAnalyticsEvent } = useAnalytics();
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string>('');
  const [viewType, setViewType] = useState<ELoginViewType>(
    ELoginViewType.register
  );

  const onDontHaveAccountClick = () => {
    setViewType(ELoginViewType.register);
  };

  const onForgotPasswordClick = () => {
    setViewType(ELoginViewType.forgotPassword);
  };
  const onBackToLoginClick = () => {
    setViewType(ELoginViewType.login);
  };
  const onAlreadyMemberClick = () => {
    fireAnalyticsEvent(ANALYTICS.EVENTS.ALREADY_A_MEMBER_BUTTON, {
      [ANALYTICS.PROPERTIES.EVENT_TYPE]: ANALYTICS.TYPES.CLICK,
      [ANALYTICS.PROPERTIES.SOURCE]: props.analyticsPayload.source,
      [ANALYTICS.PROPERTIES.IS_PREMIUM_HOOK]: isPremium(),
      ...props.analyticsPayload.additionalData,
    });
    setViewType(ELoginViewType.login);
  };

  const showRegister = () => viewType === ELoginViewType.register;

  const showLogin = () => viewType === ELoginViewType.login || showRegister();

  const showForgotPassword = () => viewType === ELoginViewType.forgotPassword;

  const showForgotPasswordSuccess = () =>
    viewType === ELoginViewType.forgotPasswordSuccess;

  const showAlreadyMemberLink = () => showLogin() && showRegister();

  const showDontHaveAccountLink = () => showLogin() && !showRegister();

  const showForgotPasswordLink = () => showDontHaveAccountLink();

  const showBackToLoginLink = () =>
    showForgotPassword() || showForgotPasswordSuccess();

  const onSuccess = async () => {
    props.onSuccess();
  };

  const getTitle = () => {
    switch (viewType) {
      case ELoginViewType.login:
        return t('login.titles.title');
      case ELoginViewType.register:
        return t('register.titles.title');
      case ELoginViewType.forgotPassword:
        return t('forgotPassword.titles.title');
      case ELoginViewType.forgotPasswordSuccess:
        return t('forgotPasswordSuccess.titles.title');
    }
  };

  const getDescription = () => {
    switch (viewType) {
      case ELoginViewType.login:
        return t('login.titles.description');
      case ELoginViewType.register:
        return t('register.titles.description');
      case ELoginViewType.forgotPassword:
        return t('forgotPassword.titles.description');
      case ELoginViewType.forgotPasswordSuccess:
        return t('forgotPasswordSuccess.titles.description');
    }
  };

  const onForgotPasswordSuccess = (email: string) => {
    setViewType(ELoginViewType.forgotPasswordSuccess);
    setForgotPasswordEmail(email);
  };
  const shouldDisplayVendorText =
    props.providersViewType === ELoginProvidersViewType.vertical;

  return (
    <div className={styles.loginWrapper}>
      {props.showTitles && (
        <div className={styles.titles}>
          <div className={styles.title}>{getTitle()}</div>
          <div className={styles.description}>
            {getDescription()}
            {showForgotPasswordSuccess() && (
              <span className={styles.forgotPasswordSuccessEmail}>
                &nbsp;{forgotPasswordEmail}
              </span>
            )}
          </div>
        </div>
      )}

      {showLogin() && (
        <>
          <LoginProviders
            source={props.analyticsPayload.source}
            onLoginSuccess={onSuccess}
            loginProvidersViewType={props.providersViewType}
            displayVendorName={shouldDisplayVendorText}
          />
          <div className={styles.or}>or</div>
          {showRegister() && (
            <Register
              source={props.analyticsPayload.source}
              onRegisterSuccess={onSuccess}
            />
          )}
          {!showRegister() && (
            <LoginEmailPassword
              source={props.analyticsPayload.source}
              onLoginSuccess={onSuccess}
            />
          )}
        </>
      )}
      {showForgotPassword() && (
        <ForgotPassword onSuccess={onForgotPasswordSuccess} />
      )}
      {showForgotPasswordSuccess() && <ForgotPasswordSuccess />}

      <div className={styles.disclaimer}>
        {showRegister() && <LoginDisclaimer />}
      </div>

      <div className={styles.bottomLinks}>
        {showAlreadyMemberLink() && (
          <div className={styles.bottomLink} onClick={onAlreadyMemberClick}>
            <Trans i18nKey="loginModalFooter.alreadyMember" />
          </div>
        )}
        {showDontHaveAccountLink() && (
          <div className={styles.bottomLink} onClick={onDontHaveAccountClick}>
            <Trans i18nKey="loginModalFooter.dontHaveAccount" />
          </div>
        )}
        {showForgotPasswordLink() && (
          <div className={styles.bottomLink} onClick={onForgotPasswordClick}>
            <Trans i18nKey="loginModalFooter.forgotPassword" />
          </div>
        )}
        {showBackToLoginLink() && (
          <div className={styles.bottomLink} onClick={onBackToLoginClick}>
            <Trans i18nKey="loginModalFooter.backToLogin" />
          </div>
        )}
      </div>
    </div>
  );
};
